import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, RefreshCw, CreditCard, Building, DollarSign, 
  AlertTriangle, CheckCircle, Clock, Calendar, ChevronDown, ChevronUp,
  ExternalLink, Loader2, TrendingUp, TrendingDown
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('pt-BR');
  } catch {
    return dateStr;
  }
};

const AdminASAAS = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Dados
  const [boletosData, setBoletosData] = useState(null);
  const [cartaoData, setCartaoData] = useState(null);
  const [asaasStatus, setAsaasStatus] = useState(null);
  
  // Expansão de seções
  const [expandVencidos, setExpandVencidos] = useState(true);
  const [expandAVencer, setExpandAVencer] = useState(true);
  const [expandCartaoPagas, setExpandCartaoPagas] = useState(false);
  const [expandCartaoPendentes, setExpandCartaoPendentes] = useState(true);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadData();
    checkAsaasStatus();
  }, [navigate]);

  const checkAsaasStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/asaas/check-ip`);
      setAsaasStatus(response.data);
    } catch (error) {
      console.error('Erro ao verificar ASAAS:', error);
      setAsaasStatus({ asaas_connected: false, message: 'Erro ao conectar' });
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Primeiro tenta buscar dados diretamente do ASAAS (independente das reservas locais)
      const allPaymentsRes = await axios.get(`${BACKEND_URL}/api/asaas/all-payments?limit=200`).catch(() => null);
      
      if (allPaymentsRes?.data?.success) {
        // Usar dados do endpoint direto do ASAAS
        const data = allPaymentsRes.data;
        setBoletosData({
          success: true,
          boletos_a_vencer: data.boletos_pendentes || [],
          boletos_vencidos: data.boletos_vencidos || [],
          total_a_vencer: data.summary?.boletos?.pendentes || 0,
          total_vencidos: data.summary?.boletos?.vencidos || 0,
          valor_a_vencer: data.summary?.boletos?.valor_pendente || 0,
          valor_vencido: data.summary?.boletos?.valor_vencido || 0
        });
        setCartaoData({
          success: true,
          parcelas_pagas: data.cartao_pagos || [],
          parcelas_pendentes: data.cartao_pendentes || [],
          total_pagas: data.summary?.cartao?.pagos || 0,
          total_pendentes: data.summary?.cartao?.pendentes || 0,
          valor_pago: data.summary?.cartao?.valor_pago || 0,
          valor_pendente: data.summary?.cartao?.valor_pendente || 0
        });
      } else {
        // Fallback: usar endpoints que dependem das reservas locais
        const [boletosRes, cartaoRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/asaas/boletos/pendentes`).catch(() => ({ data: { boletos_a_vencer: [], boletos_vencidos: [] } })),
          axios.get(`${BACKEND_URL}/api/asaas/cartao/parcelas`).catch(() => ({ data: { parcelas_pagas: [], parcelas_pendentes: [] } }))
        ]);
        
        setBoletosData(boletosRes.data);
        setCartaoData(cartaoRes.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados do ASAAS' });
    } finally {
      setLoading(false);
    }
  };

  const handleResync = async () => {
    if (!window.confirm('Isso irá ressincronizar TODOS os pagamentos do ASAAS. Pode demorar alguns minutos. Continuar?')) {
      return;
    }
    
    setSyncing(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/asaas/resync-all`);
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        loadData();
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Erro na sincronização' });
      }
    } catch (error) {
      console.error('Erro ao ressincronizar:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao ressincronizar' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncInstallments = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/asaas/sync-installments-info`);
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        loadData();
      }
    } catch (error) {
      console.error('Erro ao sincronizar parcelas:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao sincronizar' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#003366] text-white py-4 px-6 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="hover:bg-white/10 p-2 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">Integração ASAAS</h1>
              <p className="text-sm text-white/70">Gerenciamento de Cobranças e Pagamentos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {asaasStatus && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${asaasStatus.asaas_connected ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                {asaasStatus.asaas_connected ? (
                  <><CheckCircle className="w-4 h-4" /> Conectado</>
                ) : (
                  <><AlertTriangle className="w-4 h-4" /> Desconectado</>
                )}
              </div>
            )}
            <Button
              onClick={handleResync}
              disabled={syncing}
              className="bg-[#FFD700] hover:bg-[#FFC700] text-black"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Ressincronizar Tudo
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Mensagens */}
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
          </div>
        )}

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Boletos Vencidos</p>
                  <p className="text-2xl font-bold text-red-600">{boletosData?.total_vencidos || 0}</p>
                  <p className="text-xs text-gray-400">{formatPrice(boletosData?.valor_vencido)}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Boletos a Vencer</p>
                  <p className="text-2xl font-bold text-yellow-600">{boletosData?.total_a_vencer || 0}</p>
                  <p className="text-xs text-gray-400">{formatPrice(boletosData?.valor_a_vencer)}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Cartão - Pagas</p>
                  <p className="text-2xl font-bold text-green-600">{cartaoData?.total_pagas || 0}</p>
                  <p className="text-xs text-gray-400">{formatPrice(cartaoData?.valor_pago)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Cartão - Pendentes</p>
                  <p className="text-2xl font-bold text-blue-600">{cartaoData?.total_pendentes || 0}</p>
                  <p className="text-xs text-gray-400">{formatPrice(cartaoData?.valor_pendente)}</p>
                </div>
                <CreditCard className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
          </div>
        ) : (
          <Tabs defaultValue="boletos" className="space-y-4">
            <TabsList className="bg-white">
              <TabsTrigger value="boletos" className="data-[state=active]:bg-[#003366] data-[state=active]:text-white">
                <Building className="w-4 h-4 mr-2" />
                Boletos
              </TabsTrigger>
              <TabsTrigger value="cartao" className="data-[state=active]:bg-[#003366] data-[state=active]:text-white">
                <CreditCard className="w-4 h-4 mr-2" />
                Cartão de Crédito
              </TabsTrigger>
            </TabsList>

            {/* Tab Boletos */}
            <TabsContent value="boletos" className="space-y-4">
              {/* Boletos Vencidos */}
              <Card>
                <CardHeader 
                  className="cursor-pointer flex flex-row items-center justify-between py-3 bg-red-50"
                  onClick={() => setExpandVencidos(!expandVencidos)}
                >
                  <CardTitle className="text-base flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Boletos Vencidos ({boletosData?.boletos_vencidos?.length || 0})
                  </CardTitle>
                  {expandVencidos ? <ChevronUp className="w-5 h-5 text-red-700" /> : <ChevronDown className="w-5 h-5 text-red-700" />}
                </CardHeader>
                {expandVencidos && (
                  <CardContent className="p-0">
                    {boletosData?.boletos_vencidos?.length > 0 ? (
                      <div className="divide-y">
                        {boletosData.boletos_vencidos.map((boleto, idx) => (
                          <div key={idx} className="p-4 hover:bg-red-50/50 flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{boleto.customer_name}</p>
                              <p className="text-sm text-gray-600">{boleto.package_name || boleto.description}</p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Venceu: {formatDate(boleto.due_date)}
                                </span>
                                <Badge variant="destructive" className="text-xs">
                                  {boleto.days_overdue} dias atrasado
                                </Badge>
                                {boleto.installment_count > 1 && (
                                  <span className="text-gray-600">
                                    Parcela {boleto.installment}/{boleto.installment_count}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-600">{formatPrice(boleto.value)}</p>
                              {boleto.boleto_url && (
                                <a 
                                  href={boleto.boleto_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline flex items-center gap-1 justify-end mt-1"
                                >
                                  Ver Boleto <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                        Nenhum boleto vencido!
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Boletos a Vencer */}
              <Card>
                <CardHeader 
                  className="cursor-pointer flex flex-row items-center justify-between py-3 bg-yellow-50"
                  onClick={() => setExpandAVencer(!expandAVencer)}
                >
                  <CardTitle className="text-base flex items-center gap-2 text-yellow-700">
                    <Clock className="w-5 h-5" />
                    Boletos a Vencer ({boletosData?.boletos_a_vencer?.length || 0})
                  </CardTitle>
                  {expandAVencer ? <ChevronUp className="w-5 h-5 text-yellow-700" /> : <ChevronDown className="w-5 h-5 text-yellow-700" />}
                </CardHeader>
                {expandAVencer && (
                  <CardContent className="p-0">
                    {boletosData?.boletos_a_vencer?.length > 0 ? (
                      <div className="divide-y">
                        {boletosData.boletos_a_vencer.map((boleto, idx) => (
                          <div key={idx} className="p-4 hover:bg-yellow-50/50 flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{boleto.customer_name}</p>
                              <p className="text-sm text-gray-600">{boleto.package_name || boleto.description}</p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Vence: {formatDate(boleto.due_date)}
                                </span>
                                <Badge variant="outline" className={`text-xs ${boleto.days_until_due <= 3 ? 'border-red-500 text-red-600' : boleto.days_until_due <= 7 ? 'border-yellow-500 text-yellow-600' : ''}`}>
                                  {boleto.days_until_due === 0 ? 'Vence hoje' : boleto.days_until_due === 1 ? 'Vence amanhã' : `${boleto.days_until_due} dias`}
                                </Badge>
                                {boleto.installment_count > 1 && (
                                  <span className="text-gray-600">
                                    Parcela {boleto.installment}/{boleto.installment_count}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-yellow-600">{formatPrice(boleto.value)}</p>
                              {boleto.boleto_url && (
                                <a 
                                  href={boleto.boleto_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline flex items-center gap-1 justify-end mt-1"
                                >
                                  Ver Boleto <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        Nenhum boleto pendente
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </TabsContent>

            {/* Tab Cartão de Crédito */}
            <TabsContent value="cartao" className="space-y-4">
              {/* Parcelas Pendentes */}
              <Card>
                <CardHeader 
                  className="cursor-pointer flex flex-row items-center justify-between py-3 bg-blue-50"
                  onClick={() => setExpandCartaoPendentes(!expandCartaoPendentes)}
                >
                  <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                    <CreditCard className="w-5 h-5" />
                    Parcelas Pendentes ({cartaoData?.parcelas_pendentes?.length || 0})
                  </CardTitle>
                  {expandCartaoPendentes ? <ChevronUp className="w-5 h-5 text-blue-700" /> : <ChevronDown className="w-5 h-5 text-blue-700" />}
                </CardHeader>
                {expandCartaoPendentes && (
                  <CardContent className="p-0">
                    {cartaoData?.parcelas_pendentes?.length > 0 ? (
                      <div className="divide-y">
                        {cartaoData.parcelas_pendentes.map((parcela, idx) => (
                          <div key={idx} className="p-4 hover:bg-blue-50/50 flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{parcela.customer_name}</p>
                              <p className="text-sm text-gray-600">{parcela.package_name || parcela.description}</p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Vencimento: {formatDate(parcela.due_date)}
                                </span>
                                {parcela.installment_count > 1 && (
                                  <Badge variant="outline" className="text-xs">
                                    Parcela {parcela.installment}/{parcela.installment_count}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-blue-600">{formatPrice(parcela.value)}</p>
                              <Badge variant="outline" className="text-xs mt-1">
                                {parcela.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        Nenhuma parcela pendente de cartão
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              {/* Parcelas Pagas */}
              <Card>
                <CardHeader 
                  className="cursor-pointer flex flex-row items-center justify-between py-3 bg-green-50"
                  onClick={() => setExpandCartaoPagas(!expandCartaoPagas)}
                >
                  <CardTitle className="text-base flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    Parcelas Pagas ({cartaoData?.parcelas_pagas?.length || 0})
                  </CardTitle>
                  {expandCartaoPagas ? <ChevronUp className="w-5 h-5 text-green-700" /> : <ChevronDown className="w-5 h-5 text-green-700" />}
                </CardHeader>
                {expandCartaoPagas && (
                  <CardContent className="p-0">
                    {cartaoData?.parcelas_pagas?.length > 0 ? (
                      <div className="divide-y max-h-96 overflow-y-auto">
                        {cartaoData.parcelas_pagas.slice(0, 50).map((parcela, idx) => (
                          <div key={idx} className="p-4 hover:bg-green-50/50 flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{parcela.customer_name}</p>
                              <p className="text-sm text-gray-600">{parcela.package_name || parcela.description}</p>
                              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3 text-green-500" />
                                  Pago: {formatDate(parcela.paid_at)}
                                </span>
                                {parcela.installment_count > 1 && (
                                  <Badge className="text-xs bg-green-100 text-green-700">
                                    Parcela {parcela.installment}/{parcela.installment_count}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{formatPrice(parcela.value)}</p>
                            </div>
                          </div>
                        ))}
                        {cartaoData.parcelas_pagas.length > 50 && (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            Mostrando 50 de {cartaoData.parcelas_pagas.length} parcelas
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        Nenhuma parcela paga de cartão
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Ações Adicionais */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">Ações de Sincronização</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4">
            <Button
              onClick={async () => {
                setSyncing(true);
                setMessage({ type: '', text: '' });
                try {
                  // Passo 1: Sincronizar estornos direto do ASAAS (por status REFUNDED)
                  const refundRes = await axios.post(`${BACKEND_URL}/api/asaas/sync-refunds`);
                  
                  // Passo 2: Sincronizar parcelas (detecta estornos pelas parcelas com status REFUNDED)
                  const installRes = await axios.post(`${BACKEND_URL}/api/asaas/sync-installments-info`);
                  
                  let msg = '';
                  const refundUpdated = refundRes.data?.updated_reservations?.length || 0;
                  const installUpdated = installRes.data?.updated_count || 0;
                  
                  if (refundUpdated > 0 || installUpdated > 0) {
                    msg = `Sincronização concluída! ${refundUpdated + installUpdated} reserva(s) atualizada(s).`;
                    if (refundRes.data.updated_reservations?.length > 0) {
                      msg += '\n\nReservas estornadas:';
                      refundRes.data.updated_reservations.forEach(r => {
                        msg += `\n• ${r.customer_name} - ${r.package_name}`;
                      });
                    }
                  } else {
                    msg = `Sincronização concluída. Nenhuma nova reserva estornada encontrada. (${refundRes.data?.total_refunds_in_asaas || 0} estornos no ASAAS verificados)`;
                  }
                  
                  setMessage({ type: 'success', text: msg });
                  loadData();
                } catch (error) {
                  setMessage({ type: 'error', text: error.response?.data?.detail || 'Erro ao sincronizar estornos' });
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sincronizar Estornos
            </Button>
            <Button
              onClick={handleSyncInstallments}
              disabled={syncing}
              variant="outline"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sincronizar Info de Parcelas
            </Button>
            <Button
              onClick={loadData}
              disabled={loading}
              variant="outline"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Atualizar Dados
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminASAAS;
